import { Attachment, DeleteAttachmentPayload } from '@api/models'
import {
  AttachmentApi,
  useDeleteAttachmentMutation,
  useGetVendorAttachmentsQuery,
} from '@api'
import {
  ReactSelectOption,
  ReactSelectOptions,
} from '@components/common/Select/Select.types'
import { useEffect, useState } from 'react'
import { toast } from '@components/common/Toasts'

interface UseVendorAttachmentHookProps {
  vendorID: string
}

const useVendorAttachmentHook = ({ vendorID }: UseVendorAttachmentHookProps) => {
  const [options, setOptions] = useState<Attachment[]>([])
  const [selectedOption, setSelectedOption] = useState<ReactSelectOption>(null)
  const [attachment, setAttachment] = useState<{
    isLoading: boolean
    url: string
    details: Attachment
  }>(null)
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null)

  const [deleteAttachmentMutation] = useDeleteAttachmentMutation()

  const {
    isLoading: isVendorAttachmentsLoading,
    data: vendorAttachments,
  } = useGetVendorAttachmentsQuery(
    {
      vendorID,
    },
    {
      skip: !vendorID, // Skip if no vendor ID provided
    }
  )

  let selectOptions: ReactSelectOptions = options.map(a => ({
    label: a.Comment,
    value: a.FileName,
  }))

  // initialize options when vendor attachments are loaded
  useEffect(() => {
    if (isVendorAttachmentsLoading || !vendorAttachments) return

    setOptions(vendorAttachments)
    if (vendorAttachments.length > 0) {
      setSelectedOption({
        label: vendorAttachments[0].Comment,
        value: vendorAttachments[0].FileName,
      })
    } else {
      setSelectedOption(null)
    }
  }, [vendorAttachments, isVendorAttachmentsLoading])

  // download attachment and parse into blob
  useEffect(() => {
    if (selectedOption === null) {
      setAttachment(null)
    } else {
      setAttachment({ url: '', isLoading: true, details: null })
      AttachmentApi.getInstance()
        .download(selectedOption.value)
        .then((res: any) => {
          setAttachment({
            url: URL.createObjectURL(new Blob([res.data])),
            isLoading: false,
            details: vendorAttachments.find(a => a.FileName === selectedOption.value),
          })
        })
        .catch(() => {
          setAttachment(null)
        })
    }
  }, [selectedOption, vendorAttachments])

  function handleAttachmentSelection(option: ReactSelectOption) {
    setSelectedOption(option)
  }

  const deleteAttachment = async (params: DeleteAttachmentPayload) => {
    setDeletingAttachmentId(params.id)
    try {
      await deleteAttachmentMutation(params).unwrap()
      toast.success('Attachment deleted successfully')
    } catch (error) {
      toast.error('Failed to delete attachment')
      throw error
    } finally {
      setDeletingAttachmentId(null)
    }
  }

  function isAttachmentDeleting(attachmentId: string): boolean {
    return deletingAttachmentId === attachmentId
  }

  return {
    selectOptions,
    options, // Return the raw attachment options array
    isLoading: isVendorAttachmentsLoading,
    selectedOption,
    handleAttachmentSelection,
    attachment,
    vendorID,
    deleteAttachment,
    isAttachmentDeleting,
  }
}

export default useVendorAttachmentHook